import { useEffect, useState, type FormEvent } from 'react';
import { Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import { api, ApiError, type CreateOrganisationResponse, type Organisation } from '@/api/client.js';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parents: Organisation[];
  onCreated: (response: CreateOrganisationResponse) => void;
  /**
   * Wizard chain handoff. When supplied, the success view renders a
   * "Create application" button alongside the regular "Done" CTA.
   * Click hands the freshly-created org back to the parent, which
   * closes this dialog and opens the application wizard pre-seeded
   * with the new org. The parent decides where to wire it because
   * that requires also mounting CreateApplicationDialog.
   */
  onCreateApplicationAfter?: (org: Organisation) => void;
}

/**
 * Two-step dialog: collect the org details + (optional) admin email, then
 * show the result — a one-shot invitation link if an admin was invited, or
 * a simple confirmation if the new org is being administered by ancestor admins.
 */
export function CreateOrgDialog({
  open,
  onOpenChange,
  parents,
  onCreated,
  onCreateApplicationAfter,
}: Props): JSX.Element {
  const [stage, setStage] = useState<'form' | 'invite'>('form');
  const [name, setName] = useState('');
  const [parentOrgId, setParentOrgId] = useState(parents[0]?.id ?? '');
  const [adminEmail, setAdminEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<CreateOrganisationResponse | null>(null);
  const [copied, setCopied] = useState(false);

  /**
   * Sync `parentOrgId` to the first option whenever `parents` changes.
   *
   * Why: on first render `parents` is often an empty array (TanStack Query is
   * still in flight), so `useState(parents[0]?.id ?? '')` captures `''`. The
   * native `<select>` then VISUALLY displays the first `<option>` once parents
   * arrives — but the React state stays `''`. Submitting the form hits the
   * "Pick a parent organisation" error even though the dropdown clearly shows
   * one selected. This effect closes that gap.
   *
   * We only auto-select when `parentOrgId` doesn't match any current option,
   * so a user who deliberately picks a different parent doesn't get reset
   * if `parents` re-renders for an unrelated reason.
   */
  useEffect(() => {
    if (parents.length === 0) return;
    if (!parents.some((p) => p.id === parentOrgId)) {
      setParentOrgId(parents[0]!.id);
    }
  }, [parents, parentOrgId]);

  function resetAndClose() {
    setStage('form');
    setName('');
    setAdminEmail('');
    setError(null);
    setResponse(null);
    setCopied(false);
    onOpenChange(false);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!parentOrgId) {
      setError('Pick a parent organisation.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // adminEmail is optional — empty string means "no invitation, manage via parent".
      const trimmed = adminEmail.trim();
      const res = await api.organisations.create({
        name,
        parentOrgId,
        ...(trimmed ? { adminEmail: trimmed } : {}),
      });
      setResponse(res);
      setStage('invite');
      onCreated(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create organisation. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * "Create application" handoff. Closes this dialog and signals to
   * the parent so it can open the app wizard pre-seeded with the
   * just-created org. State reset happens through resetAndClose so a
   * subsequent New-organisation click doesn't see stale form data.
   */
  function continueToAppWizard() {
    if (!response || !onCreateApplicationAfter) return;
    const org = response.organisation;
    setStage('form');
    setName('');
    setAdminEmail('');
    setError(null);
    setResponse(null);
    setCopied(false);
    onOpenChange(false);
    onCreateApplicationAfter(org);
  }

  async function copyLink() {
    // copyLink is only rendered when response.invitation is non-null, but TS
    // doesn't know that — guard explicitly.
    if (!response?.invitation) return;
    await navigator.clipboard.writeText(response.invitation.acceptUrl);
    setCopied(true);
    toast.success('Invite link copied');
    setTimeout(() => setCopied(false), 2_000);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) resetAndClose();
        else onOpenChange(o);
      }}
    >
      <DialogContent>
        {stage === 'form' ? (
          <>
            <DialogHeader>
              <DialogTitle>New organisation</DialogTitle>
              <DialogDescription>
                Creates a child organisation. Optionally invite an admin now — or leave it empty and
                let an admin of the parent org manage it via cascade.
              </DialogDescription>
            </DialogHeader>
            <form className="space-y-4" onSubmit={submit}>
              <div className="space-y-1.5">
                <Label htmlFor="parent">Parent organisation</Label>
                <select
                  id="parent"
                  value={parentOrgId}
                  onChange={(e) => setParentOrgId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {parents.map((p) => (
                    <option key={p.id} value={p.id}>
                      {indentForPath(p.path)}{p.name} <span className="text-muted-foreground">/{p.slug}</span>
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="name">Organisation name</Label>
                <Input
                  id="name"
                  required
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Customer A"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="admin">
                  Admin email <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Input
                  id="admin"
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="admin@customer-a.example"
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty if this org will be managed by an admin of the parent org. You can invite
                  one later from the Members tab. When supplied, they receive a one-shot invitation
                  link to sign in for the first time.
                </p>
              </div>
              {error && (
                <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={resetAndClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : adminEmail.trim() ? (
                    'Create + invite'
                  ) : (
                    'Create'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : response ? (
          response.invitation ? (
            <>
              <DialogHeader>
                <DialogTitle>{response.organisation.name} created</DialogTitle>
                <DialogDescription>
                  Share this one-shot invitation link with{' '}
                  <span className="font-medium text-foreground">{response.invitation.email}</span>. It's
                  shown once and expires {new Date(response.invitation.expiresAt).toLocaleDateString()}.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="rounded-md border border-border bg-background px-3 py-2 font-mono text-xs break-all">
                  {response.invitation.acceptUrl}
                </div>
                <Button onClick={copyLink} variant="secondary" className="w-full">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
                <p className="text-xs text-muted-foreground">
                  DeployIt doesn't email this yet. Paste it into your channel of choice.
                </p>
              </div>
              <DialogFooter className="sm:justify-between">
                <Button variant="ghost" onClick={resetAndClose}>
                  Done
                </Button>
                {onCreateApplicationAfter && (
                  <Button onClick={continueToAppWizard}>
                    Create application in {response.organisation.name}
                  </Button>
                )}
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{response.organisation.name} created</DialogTitle>
                <DialogDescription>
                  No admin was invited, so the org has no direct members yet. It's administered by anyone
                  with admin on a parent org (cascade). You can invite an admin later from this org's
                  Members tab.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="sm:justify-between">
                <Button variant="ghost" onClick={resetAndClose}>
                  Done
                </Button>
                {onCreateApplicationAfter && (
                  <Button onClick={continueToAppWizard}>
                    Create application in {response.organisation.name}
                  </Button>
                )}
              </DialogFooter>
            </>
          )
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function indentForPath(path: string): string {
  if (path === '/') return '';
  return '  '.repeat(path.split('/').filter(Boolean).length) + '↳ ';
}
